import { ChevronLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
	AttributesModal,
	TemplateBoolean,
	TemplateCreateable,
	TemplateField,
	TemplateReactSelect,
} from '../../components/Attributes/Attributes_old';
import { MainContent, PageHeading } from '../../components/Shell/Content';
import Button from '../../components/Button';
import BetaAttributes, { configurePayload } from '../../components/Attributes';
import { FIELD_OPTIONS } from '../../components/helpers/constants';
import Modal from '../../components/Shell/Modal';

import { addModuleFields, getModuleFieldsBeta } from '../../api/modules';
import { addAssets, getAsset, updateAsset } from '../../api';

import useSearchParams from '../../hooks/useSearchParams';
import usePrevious from '../../hooks/usePrevious';
import useRequired from '../../hooks/useRequired';
import useAsync from '../../hooks/useAsync';
import useSettings from '../../hooks/useSettings';

const ASSETS_MODULE_ID = process.env.REACT_APP_MODULE_ID_ASSETS;

export default () => {
	const navigate = useNavigate();
	const { get: getSetting, set: setSetting } = useSettings();

	const { id } = useParams();
	const [modalDetails, setOpenModal] = useState(false);
	const [customAttrModalOpen, setCustomAttrModalOpen] = useState(false);
	const { location } = useSearchParams();

	const [additionalFields, setAdditionalFields] = useState([]);
	const [form, setForm] = useState({});
	const [customAttributeForm, setCustomerAttributeForm] = useState({});
	const [updatedForm, setUpdatedForm] = useState({});

	const { value: item, execute: getItem } = useAsync(getAsset, {
		immediate: false,
	});

	const { execute: onUpdateItem } = useAsync(updateAsset, {
		immediate: false,
	});

	const { execute: onAddItem } = useAsync(addAssets, {
		immediate: false,
		showNotification: { error: 'raw' },
	});

	const { value: availableModuleFields } = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: ASSETS_MODULE_ID, active: false },
	});

	const {
		value: moduleFields,
		execute: onRefreshModuleFields,
		status: isLoadingModuleFields,
	} = useAsync(getModuleFieldsBeta, {
		defaultPayload: { id: ASSETS_MODULE_ID, active: true },
	});

	const { execute: onAddModuleField, status: addingField } = useAsync(
		addModuleFields,
		{
			immediate: false,
		},
	);

	useEffect(() => {
		setCustomAttrModalOpen(false);
	}, [additionalFields]);

	const prevCustomAttrModalOpen = usePrevious(customAttrModalOpen);

	useEffect(() => {
		if (!customAttrModalOpen && prevCustomAttrModalOpen) {
			setCustomerAttributeForm({});
		}
	}, [customAttrModalOpen]);

	useEffect(() => {
		const categoryId =
			updatedForm?.category?.value?.value || form?.category?.value;

		if (categoryId) {
			onRefreshModuleFields(
				{
					id: ASSETS_MODULE_ID,
					active: true,
					categoryId,
				},
				null,
				{ silent: true },
			);
		}
	}, [updatedForm?.category?.value?.value, form?.category?.value]);

	useEffect(() => {
		if (id) {
			getItem({ id });
		}
	}, []);

	useEffect(() => {
		if (item?.custom_attributes?.length) {
			setAdditionalFields([
				...additionalFields,
				...item?.custom_attributes?.map((field) => field?.attributeFieldId),
			]);
		}
	}, [item, moduleFields]);

	useEffect(() => {
		if (item && moduleFields) {
			// 1. On update attempt, create update object
			let update = {};

			const CONVERTER = {};

			// Update update object with event fields
			[
				...moduleFields?.combined,
				...(availableFields ? availableFields : []),
			]?.map((field) => {
				const key = CONVERTER[field.key] || field?.key;
				if (item[key]) {
					update[field?.key] = {
						fieldId: field?.id,
						value: item[key],
					};
				} else if (item?.custom_attributes?.length) {
					const attr = item?.custom_attributes?.find(
						(attr) => attr?.attributeFieldId === field?.id,
					);

					if (attr) {
						update[key] = {
							id: attr?.id,
							fieldId: field?.id,
							value: attr?.value,
						};
					}
				}
			});

			// Custom functions()
			if (update.manufacturer) {
				update['manufacturer'] = {
					...update?.manufacturer,
					value: update?.manufacturer.value?.id,
				};
			}

			if (item.tag) {
				update['tag'] = {
					value: item.tag,
				};
			}

			if (update.manufacturer) {
				update['model'] = {
					...update?.model,
					value: update?.model.value?.id,
				};
			}

			if (update.location) {
				update['location'] = {
					...update?.location,
					value: update?.location.value?.id,
				};
			}

			if (update.category) {
				update['category'] = {
					...update?.category,
					value: update?.category.value?.id,
				};
			}

			if (update.tags) {
				update['tags'] = {
					...update?.tags,
					value: update?.tags?.value,
				};
			}

			setForm({ ...update });
		}
	}, [item, moduleFields]);

	useEffect(() => {
		const locationModuleField = moduleFields?.standard?.find(
			(o) => o.key === 'location',
		);
		if (location && locationModuleField) {
			setForm({
				location: {
					field: locationModuleField?.id,
					value: location,
				},
			});

			navigate(0, { replace: true });
		}
	}, [location, moduleFields]);

	const isEditing = !!id;
	const allValues = Object.keys({ ...form, ...updatedForm });
	const requiredValues = ['manufacturer', 'model', 'serialNumber'];

	const availableFields = availableModuleFields?.standard?.filter((x) => {
		return moduleFields?.category?.filter((o) => o.id === x.id).length === 0;
	});

	const hasAllRequiredValues = useRequired(
		{ ...form, ...updatedForm },
		moduleFields?.combined
			?.filter((field) => field.isRequired)
			.map((field) => field.key),
	);

	const allCustomRequired =
		customAttributeForm?.id ||
		(customAttributeForm?.label && customAttributeForm?.type);

	const areAssetTagsEnabled = getSetting('assetTags');

	return (
		<>
			<MainContent>
				<PageHeading divider={false}>
					<div>
						{isEditing ? (
							<>
								<div
									className="text-gray-400 flex items-center text-xs font-normal mb-4 cursor-pointer"
									onClick={() => navigate(`/assets/${id}/overview`)}
								>
									<ChevronLeftIcon className="w-4 h-4 text-gray-400 mr-3" />{' '}
									Back to asset
								</div>
								Update asset
							</>
						) : (
							<>
								<div
									className="text-gray-400 flex items-center text-xs font-normal mb-4 cursor-pointer"
									onClick={() => navigate('/assets')}
								>
									<ChevronLeftIcon className="w-4 h-4 text-gray-400 mr-3" />{' '}
									Back to all assets
								</div>
								Add a new asset
							</>
						)}
					</div>
				</PageHeading>

				<div className="mt-10 grid grid-cols-1 gap-6 gap-x-8 sm:grid-cols-2">
					{[...(moduleFields?.standard || []), ...(availableFields || [])]
						//	?.filter((field) => skipModuleKeys.indexOf(field.key) === -1)
						?.filter(
							(field) =>
								field?.isActive || additionalFields?.includes(field?.id),
						)
						?.map((field) => (
							<BetaAttributes
								field={field}
								onChange={(index, value, fullValue) => {
									if (value || fullValue) {
										const originalAttribute = form[index];
										let updatedValue = originalAttribute?.value;

										if (fullValue) {
											updatedValue = fullValue;
										} else {
											if (updatedValue && Array.isArray(value)) {
												updatedValue = [
													...(Array.isArray(updatedValue)
														? updatedValue
														: [updatedValue]),
													...value,
												];
											} else {
												updatedValue = value;
											}
										}

										setUpdatedForm({
											...updatedForm,
											[index]: {
												...originalAttribute,
												value: updatedValue,
												fieldId: field.id,
											},
										});
									} else {
										let updated = { ...updatedForm };
										updated[index] = {
											...updated[index],
											value: null,
										};

										if (index === 'category') {
											delete updated['checklist'];
										}

										setUpdatedForm(updated);
									}
								}}
								form={{ ...form, ...updatedForm }}
								updatedForm={updatedForm}
								setForm={setUpdatedForm}
								moduleId={ASSETS_MODULE_ID}
							/>
						))}
					{areAssetTagsEnabled && (
						<TemplateField
							span={'sm:col-span-1'}
							name="Scipods Tag"
							value={
								updatedForm?.tag ? updatedForm?.tag?.value : form?.tag?.value
							}
							onChange={(index, tag) => {
								setUpdatedForm({ ...updatedForm, tag: { value: tag } });
							}}
							maxLength={4}
						/>
					)}
				</div>

				<div className="mt-5 flex justify-end">
					<Button
						type="ghost"
						Icon={PlusIcon}
						onClick={() => setCustomAttrModalOpen(true)}
					>
						Add Custom Attribute
					</Button>
				</div>

				<div className="flex justify-end mt-8 px-8">
					{isEditing ? (
						<Button
							type={hasAllRequiredValues ? 'primary' : 'disabled'}
							disabled={
								!hasAllRequiredValues ||
								!Object.keys(updatedForm)?.length ||
								(updatedForm?.tag && !updatedForm?.tag?.value)
							}
							onClick={() => {
								onUpdateItem(
									{
										id,
										...configurePayload(updatedForm, moduleFields?.combined, {
											bucket: 'assets',
											raw: {
												...(updatedForm?.tag
													? { tag: updatedForm?.tag?.value }
													: {}),
											},
										}),
									},
									() => navigate(`/assets/${id}/overview`),
								);
							}}
						>
							Save
						</Button>
					) : (
						<Button
							type={hasAllRequiredValues ? 'primary' : 'disabled'}
							disabled={!hasAllRequiredValues}
							onClick={() =>
								onAddItem(
									configurePayload(updatedForm, moduleFields?.combined, {
										bucket: 'assets',
									}),
									(payload) => {
										navigate(`/assets/${payload?.id}/overview`);
									},
								)
							}
						>
							Add
						</Button>
					)}
				</div>
			</MainContent>
			{/* Modal */}
			<AttributesModal
				details={modalDetails}
				onClose={() => setOpenModal(null)}
			/>

			<Modal
				open={customAttrModalOpen}
				title={'Select or Create Custom Attribute'}
				onClose={() => setCustomAttrModalOpen(false)}
			>
				<div className="grid gap-y-3">
					<TemplateCreateable
						options={availableFields?.map((field) => ({
							label: field?.label,
							value: field?.id,
						}))}
						onChange={(index, selectedCustomField, raw) => {
							if (raw) {
								setCustomerAttributeForm({
									...customAttributeForm,
									...(raw?.__isNew__
										? { label: raw?.label }
										: { id: raw?.value }),
								});
							} else {
								setCustomerAttributeForm({});
							}
						}}
						skip={additionalFields}
						value={customAttributeForm?.label || customAttributeForm?.id}
					/>

					{customAttributeForm?.label ? (
						<>
							<TemplateReactSelect
								name="Type"
								options={FIELD_OPTIONS}
								onChange={(index, type) =>
									setCustomerAttributeForm({ ...customAttributeForm, type })
								}
								value={customAttributeForm?.type}
							/>
							<TemplateBoolean
								span={'sm:col-span-2'}
								name="Required"
								className="mr-3"
								onChange={(index, required) =>
									setCustomerAttributeForm({ ...customAttributeForm, required })
								}
								value={customAttributeForm?.required}
							/>
							<TemplateBoolean
								span={'sm:col-span-2'}
								name="Searchable"
								onChange={(index, searchable) =>
									setCustomerAttributeForm({
										...customAttributeForm,
										searchable,
									})
								}
								value={customAttributeForm?.searchable}
							/>
						</>
					) : null}
				</div>

				<div className="flex justify-end mt-6">
					<Button
						disabled={!allCustomRequired}
						onClick={() =>
							customAttributeForm?.id
								? setAdditionalFields([
										...additionalFields,
										customAttributeForm?.id,
									])
								: onAddModuleField(
										{
											id: ASSETS_MODULE_ID,
											name: customAttributeForm?.label,
											type: customAttributeForm?.type?.value,
											searchable: customAttributeForm?.searchable,
											required: customAttributeForm?.required,
											active: true,
										},
										(response) => {
											onRefreshModuleFields(
												{ id: ASSETS_MODULE_ID },
												() =>
													setAdditionalFields([
														...additionalFields,
														response?.id,
													]),
												{
													silent: true,
												},
											);
										},
									)
						}
					>
						Add
					</Button>
				</div>
			</Modal>
		</>
	);
};
